/**
 * #644 Slice D: the owner's workforce questions, answered from one canonical snapshot.
 *
 * The snapshot is `public.workforce_snapshot` - every unretired Worker as working, available
 * or unavailable with the canonical reason (machine headroom, exact offering on that machine,
 * out_of_usage / signed_out, session state), its current session and machine, the configured
 * offering and the current Actual (null = Unknown). Nothing here decides availability; it
 * only says what the snapshot says. Configured is never called Actual, and a seat is never
 * called a lane.
 *
 * The grammar mirrors brunel_private.main_command_workforce_question exactly (shared fixture
 * supabase/tests/issue-644-workforce-grammar.json): the ingress writes these questions without
 * a project so no planner drafts them, and this is what answers them.
 */

export type WorkforceQuestion = 'lanes_available' | 'worker_inventory';

export interface WorkforceWorker {
  readonly workerId: string;
  readonly number: number;
  readonly name: string;
  readonly state: 'working' | 'available' | 'unavailable';
  /** Why a Worker is unavailable: no_session, session_offline, machine_offline, machine_held, runtime_not_on_machine, out_of_usage, signed_out, session_<status>. */
  readonly reason: string | null;
  readonly holdingBack: string | null;
  readonly availableFrom: string | null;
  readonly sessionId: string | null;
  readonly nodeId: string | null;
  readonly machine: string | null;
  readonly mode: 'auto' | 'pinned' | null;
  readonly configuredOfferingKey: string | null;
  readonly configuredRuntimeKey: string | null;
  readonly configuredRuntimeLabel: string | null;
  readonly configuredModelKey: string | null;
  readonly configuredEffortKey: string | null;
  readonly actualRuntimeKey: string | null;
  readonly actualModelKey: string | null;
  readonly actualEffortKey: string | null;
  readonly activity: { readonly taskTitle: string; readonly projectName: string } | null;
}

export interface WorkforceSnapshot {
  readonly asOf: string;
  readonly counts: { readonly total: number; readonly available: number; readonly working: number; readonly unavailable: number };
  readonly truncated: boolean;
  readonly workers: readonly WorkforceWorker[];
}

/** 'lanes_available' | 'worker_inventory' | null - the SQL grammar, word for word. */
export function workforceQuestion(content: string): WorkforceQuestion | null {
  let n = content.trim().toLowerCase();
  if (n.length === 0 || n.length > 160) return null;
  n = n.replace(/’/g, "'").replace(/[^a-z0-9 ]+/g, ' ').replace(/ +/g, ' ').trim();
  n = n.replace(/^(hey |hi |ok |okay )?(brunel |overseer )?(please )?/, '');
  if (/^how many (worker )?(lanes?|workers?) (do we have|have we got|are there|are|do i have|have i got|is there|we have)( (available|free|idle|open|ready|usable))?( (now|right now|at the moment))?$/.test(n)
    || /^how many (worker )?(lanes?|workers?) (are )?(available|free|idle|open|ready|usable)( (now|right now|at the moment))?$/.test(n)
    || /^(what|which) (worker )?lanes (are|do we have) (available|free|ready|open)( (now|right now))?$/.test(n)) {
    return 'lanes_available';
  }
  if (/^which workers are (working|idle|busy|free|available|unavailable)( (and |or )?(working|idle|busy|free|available|unavailable))*( and what is each( one| worker| of them)? doing)?( (now|right now))?$/.test(n)
    || /^what is (each|every) worker doing( (now|right now))?$/.test(n)
    || /^(workforce|worker|lane) (status|inventory|summary)$/.test(n)) {
    return 'worker_inventory';
  }
  return null;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function runtimeName(worker: WorkforceWorker): string {
  return worker.configuredRuntimeLabel ?? worker.configuredRuntimeKey ?? 'no configured runtime';
}

function machineName(worker: WorkforceWorker): string {
  return worker.machine ?? 'no machine';
}

/** The canonical reason, said plainly. */
export function unavailableReason(worker: WorkforceWorker): string {
  const runtime = runtimeName(worker);
  const machine = machineName(worker);
  switch (worker.reason) {
    case 'no_session': return 'it has no current session on any computer';
    case 'session_offline': return `its session on ${machine} is offline`;
    case 'machine_offline': return `${machine} is not reporting in`;
    case 'machine_held': return `${machine} is holding back new work${worker.holdingBack === null ? '' : ` (${worker.holdingBack.replace('_', ' ')})`}`;
    case 'runtime_not_on_machine': return `${runtime} is not on ${machine}`;
    case 'out_of_usage': return `${runtime} on ${machine} is out of usage${worker.availableFrom === null ? '' : ` until ${worker.availableFrom}`}`;
    case 'signed_out': return `${runtime} on ${machine} is signed out`;
    default:
      return worker.reason !== null && worker.reason.startsWith('session_')
        ? `its session on ${machine} is ${worker.reason.slice('session_'.length).replace('_', ' ')}`
        : 'its availability is unknown';
  }
}

/** Actual only from current Actual evidence; otherwise Unknown. */
export function actualLabel(worker: WorkforceWorker): string {
  if (worker.actualRuntimeKey === null) return 'Actual Unknown';
  return `Actual ${worker.actualRuntimeKey}${worker.actualModelKey === null ? '' : ` / ${worker.actualModelKey}`}`;
}

function configuredLabel(worker: WorkforceWorker): string {
  if (worker.configuredOfferingKey === null) return 'no configured runtime';
  return `configured ${runtimeName(worker)} (${worker.configuredOfferingKey})`;
}

function laneLine(worker: WorkforceWorker): string {
  return `Worker ${worker.number} (${runtimeName(worker)} on ${machineName(worker)})`;
}

/** "How many lanes do we have available?" */
export function lanesAnswer(snapshot: WorkforceSnapshot): string {
  const available = snapshot.workers.filter((w) => w.state === 'available');
  const working = snapshot.workers.filter((w) => w.state === 'working');
  const unavailable = snapshot.workers.filter((w) => w.state === 'unavailable');
  const lines = [
    `Right now ${plural(snapshot.counts.available, 'lane is', 'lanes are')} available${available.length === 0 ? '.' : `: ${available.map(laneLine).join(', ')}.`}`,
    `${plural(snapshot.counts.working, 'is', 'are')} working${working.length === 0 ? '.' : `: ${working.map((w) => `Worker ${w.number}`).join(', ')}.`}`,
    `${plural(snapshot.counts.unavailable, 'is', 'are')} unavailable${unavailable.length === 0 ? '.' : `: ${unavailable.map((w) => `Worker ${w.number} - ${unavailableReason(w)}`).join('; ')}.`}`,
  ];
  if (snapshot.truncated) lines.push(`Only the first ${snapshot.workers.length} of ${snapshot.counts.total} Workers are listed.`);
  return lines.join('\n');
}

/** "Which Workers are working, idle, unavailable, and what is each doing?" */
export function inventoryAnswer(snapshot: WorkforceSnapshot): string {
  if (snapshot.counts.total === 0) return 'There are no Workers yet.';
  const lines = [`Workers right now: ${snapshot.counts.working} working, ${snapshot.counts.available} idle and available, ${snapshot.counts.unavailable} unavailable.`];
  for (const worker of snapshot.workers) {
    const state = worker.state === 'working'
      ? `working on “${worker.activity?.taskTitle ?? 'a task'}”${worker.activity === null ? '' : ` in ${worker.activity.projectName}`}`
      : worker.state === 'available' ? 'idle, available' : `unavailable - ${unavailableReason(worker)}`;
    lines.push(`Worker ${worker.number} · ${machineName(worker)} · ${configuredLabel(worker)} · ${actualLabel(worker)} · ${state}`);
  }
  if (snapshot.truncated) lines.push(`Only the first ${snapshot.workers.length} of ${snapshot.counts.total} Workers are listed.`);
  return lines.join('\n');
}

/** The answer to a workforce question, or the honest reason there is none. */
export function workforceAnswer(question: WorkforceQuestion, snapshot: WorkforceSnapshot | null | undefined): string {
  if (snapshot === null || snapshot === undefined) {
    return 'I could not read the workforce just now, so I will not guess. Ask again in a moment.';
  }
  return question === 'lanes_available' ? lanesAnswer(snapshot) : inventoryAnswer(snapshot);
}
